import React, { useState, useRef, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, Space, message, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSuiteTour } from '@/hooks/useSuiteTour';
import { 
  QuestionCircleOutlined,
  BookOutlined,
  VideoCameraOutlined,
  MessageOutlined,
  HomeOutlined,
  TeamOutlined,
  UserOutlined,
  DollarCircleOutlined,
  ToolOutlined,
  FileTextOutlined,
  PlayCircleOutlined,
  RocketOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const HelpPage: React.FC = () => {
  const { t } = useTranslation(['help', 'common']);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState('tours');
  
  const { startSuiteTour } = useSuiteTour();

  // Stats slider functions

  const handleScroll = () => {
    if (sliderRef.current) {
      // Handle scroll logic
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Get icon for each health check

  // Suite tours configuration
  const suiteTours = [
    {
      id: 'resource-management',
      title: t('help:suites.resourceManagement.title'),
      description: t('help:suites.resourceManagement.description'),
      icon: <HomeOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      color: '#1890ff',
      pages: ['properties', 'residents', 'employees', 'vendors', 'assets'],
      estimatedTime: '15-20 min'
    },
    {
      id: 'resident-actions',
      title: t('help:suites.residentActions.title'),
      description: t('help:suites.residentActions.description'),
      icon: <TeamOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      color: '#52c41a',
      pages: ['residents'],
      estimatedTime: '8-10 min'
    },
    {
      id: 'employee-actions',
      title: t('help:suites.employeeActions.title'),
      description: t('help:suites.employeeActions.description'),
      icon: <UserOutlined style={{ fontSize: '24px', color: '#fa8c16' }} />,
      color: '#fa8c16',
      pages: ['employees'],
      estimatedTime: '8-10 min'
    },
    {
      id: 'finance-suite',
      title: t('help:suites.finance.title'),
      description: t('help:suites.finance.description'),
      icon: <DollarCircleOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
      color: '#722ed1',
      pages: ['finance'],
      estimatedTime: '12-15 min'
    },
    {
      id: 'maintenance-suite',
      title: t('help:suites.maintenance.title'),
      description: t('help:suites.maintenance.description'),
      icon: <ToolOutlined style={{ fontSize: '24px', color: '#eb2f96' }} />,
      color: '#eb2f96',
      pages: ['maintenance'],
      estimatedTime: '10-12 min'
    },
    {
      id: 'document-management',
      title: t('help:suites.documents.title'),
      description: t('help:suites.documents.description'),
      icon: <FileTextOutlined style={{ fontSize: '24px', color: '#13c2c2' }} />,
      color: '#13c2c2',
      pages: ['documents'],
      estimatedTime: '8-10 min'
    },
    {
      id: 'community-suite',
      title: t('help:suites.community.title'),
      description: t('help:suites.community.description'),
      icon: <MessageOutlined style={{ fontSize: '24px', color: '#faad14' }} />,
      color: '#faad14',
      pages: ['community'],
      estimatedTime: '10-12 min'
    },
  ];

  const renderToursTab = () => (
    <div>
      {/* Welcome Section */}
      <Card style={{ marginBottom: 24, textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <RocketOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
            <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
              {t('help:tours.welcome.title')}
            </Title>
            <Paragraph style={{ fontSize: '16px', color: '#666', margin: '8px 0 0 0' }}>
              {t('help:tours.welcome.description')}
            </Paragraph>
          </div>
          
          <Button 
            type="primary" 
            size="large" 
            icon={<PlayCircleOutlined />}
            onClick={() => {
              startSuiteTour('resource-management');
            }}
            style={{ 
              height: '48px', 
              fontSize: '16px',
              background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
            }}
          >
            {t('help:tours.startTour')}
          </Button>
        </Space>
      </Card>

      {/* Suite Tours Grid */}
        <Row gutter={[16, 16]}>
          {suiteTours.map((suite) => (
            <Col xs={24} sm={12} md={8} lg={8} xl={8} key={suite.id}>
              <Card
                className={`suite-tour-item ${suite.id}`}
                style={{
                  height: '220px',
                  border: '1px solid #d9d9d9',
                  transition: 'all 0.3s ease',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                }}
                styles={{
                  body: { 
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }
                }}
              >
                {/* Card Header */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  marginBottom: 16,
                  minHeight: '40px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {suite.icon}
                    <Text strong style={{ fontSize: '16px', margin: 0, lineHeight: '1.2' }}>
                      {suite.title}
                    </Text>
                  </div>
                </div>

                {/* Card Body */}
                <div style={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  marginBottom: 16
                }}>
                  <Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.4', textAlign: 'center', marginBottom: 8 }}>
                    {suite.description}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '11px', textAlign: 'center', color: suite.color }}>
                    ⏱️ {suite.estimatedTime}
                  </Text>
                </div>

                {/* Card Footer */}
                <div style={{ marginTop: 'auto' }}>
                  <Button
                    type="primary"
                    size="middle"
                    block
                    icon={<PlayCircleOutlined />}
                    onClick={() => {
                      if (suite.id === 'resource-management') {
                        startSuiteTour('resource-management');
                      } else {
                        message.info(`${suite.title} tour - Coming soon!`);
                      }
                    }}
                    style={{
                      fontSize: '13px',
                      height: '36px',
                      borderRadius: '6px',
                      background: `linear-gradient(135deg, ${suite.color} 0%, ${suite.color}dd 100%)`,
                      borderColor: suite.color,
                    }}
                  >
                    {t('help:suites.startTour')}
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
    </div>
  );

  const renderGuidesTab = () => (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 16 }}>
          {t('help:guides.title')}
        </Title>
        <Paragraph>
          {t('help:guides.description')}
        </Paragraph>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{ height: '200px' }}
            actions={[
              <Button type="link" key="read">
                {t('help:guides.gettingStarted.button')}
              </Button>
            ]}
          >
            <div style={{ textAlign: 'center' }}>
              <BookOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }} />
              <Title level={4} style={{ marginBottom: '8px' }}>
                {t('help:guides.gettingStarted.title')}
              </Title>
              <Text type="secondary">
                {t('help:guides.gettingStarted.description')}
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{ height: '200px' }}
            actions={[
              <Button type="link" key="watch">
                {t('help:guides.videoTutorials.button')}
              </Button>
            ]}
          >
            <div style={{ textAlign: 'center' }}>
              <VideoCameraOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '12px' }} />
              <Title level={4} style={{ marginBottom: '8px' }}>
                {t('help:guides.videoTutorials.title')}
              </Title>
              <Text type="secondary">
                {t('help:guides.videoTutorials.description')}
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{ height: '200px' }}
            actions={[
              <Button type="link" key="faq">
                {t('help:guides.faq.button')}
              </Button>
            ]}
          >
            <div style={{ textAlign: 'center' }}>
              <QuestionCircleOutlined style={{ fontSize: '32px', color: '#faad14', marginBottom: '12px' }} />
              <Title level={4} style={{ marginBottom: '8px' }}>
                {t('help:guides.faq.title')}
              </Title>
              <Text type="secondary">
                {t('help:guides.faq.description')}
              </Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            style={{ height: '200px' }}
            actions={[
              <Button type="link" key="contact">
                {t('help:guides.contactSupport.button')}
              </Button>
            ]}
          >
            <div style={{ textAlign: 'center' }}>
              <MessageOutlined style={{ fontSize: '32px', color: '#722ed1', marginBottom: '12px' }} />
              <Title level={4} style={{ marginBottom: '8px' }}>
                {t('help:guides.contactSupport.title')}
              </Title>
              <Text type="secondary">
                {t('help:guides.contactSupport.description')}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <div style={{ padding: '24px' }}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'tours',
            label: t('help:tabs.tours'),
            children: renderToursTab(),
          },
          {
            key: 'guides',
            label: t('help:tabs.guides'),
            children: renderGuidesTab(),
          },
        ]}
      />
    </div>
  );
};

export default HelpPage;